/**
 * Botón de nota de voz.
 * Click = grabar/detener; mantener / clic derecho = panel (mismo gesto que DSA).
 */
import React, { PureComponent } from 'react'
import Icon from '@mdi/react'
import { IconButton, Tooltip } from '@material-ui/core'
import { mdiMicrophone, mdiMicrophoneOff } from '@mdi/js'

const DEFAULT_ICON = 0.95
/** Mismo umbral que DsaMenu. */
const LONG_PRESS_MS = 420

/**
 * @typedef {{
 *   holdToTalk?: boolean,
 *   recording?: boolean,
 *   busy?: boolean,
 *   disabled?: boolean,
 *   iconSize?: number,
 *   color?: string,
 *   onStart?: () => void,
 *   onStop?: () => void,
 *   onToggle?: () => void,
 *   onLongPress?: () => void,
 *   className?: string,
 *   style?: object,
 *   showSubmenuHint?: boolean,
 * }} VoiceNoteButtonProps
 */

class VoiceNoteButton extends PureComponent {
  /** @param {VoiceNoteButtonProps} props */
  constructor(props) {
    super(props)
    this._longPressTimer = null
    this._longPressFired = false
    this._pointerId = null
    this._holding = false
    this._ignoreClickUntil = 0
  }

  componentWillUnmount() {
    this.clearLongPress()
  }

  clearLongPress = () => {
    if (this._longPressTimer) {
      clearTimeout(this._longPressTimer)
      this._longPressTimer = null
    }
  }

  openPanel = () => {
    const { onLongPress } = this.props
    if (typeof onLongPress !== 'function') return
    this._longPressFired = true
    this._ignoreClickUntil = Date.now() + 500
    onLongPress()
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate(12)
      } catch (err) {
        /* ignore */
      }
    }
  }

  handlePointerDown = (e) => {
    const { holdToTalk, disabled, busy, onStart, onLongPress } = this.props
    if (disabled || busy) return

    if (holdToTalk) {
      if (e.button != null && e.button !== 0) return
      e.preventDefault()
      this._holding = true
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch (err) {
        /* ignore */
      }
      if (onStart) onStart()
      return
    }

    if (typeof onLongPress !== 'function') return
    if (e.button != null && e.button !== 0) return

    this._longPressFired = false
    this._pointerId = e.pointerId
    this.clearLongPress()
    this._longPressTimer = setTimeout(() => {
      this._longPressTimer = null
      this.openPanel()
    }, LONG_PRESS_MS)
  }

  handlePointerUp = (e) => {
    const { holdToTalk, recording, onStop } = this.props
    if (holdToTalk) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch (err) {
        /* ignore */
      }
      if (this._holding || recording) {
        this._holding = false
        if (onStop) onStop()
      }
      return
    }

    this.clearLongPress()
    if (this._pointerId != null && e.pointerId !== this._pointerId) return
    this._pointerId = null
  }

  handlePointerCancel = () => {
    const { holdToTalk, recording, onStop } = this.props
    this.clearLongPress()
    this._pointerId = null
    if (holdToTalk && (this._holding || recording)) {
      this._holding = false
      if (onStop) onStop()
    }
  }

  handleClick = (e) => {
    const { holdToTalk, disabled, busy, onToggle } = this.props
    if (holdToTalk) {
      e.preventDefault()
      return
    }
    if (disabled || busy) return

    if (this._longPressFired || Date.now() < this._ignoreClickUntil) {
      e.preventDefault()
      e.stopPropagation()
      this._longPressFired = false
      return
    }
    if (onToggle) onToggle()
  }

  handleContextMenu = (e) => {
    const { holdToTalk, disabled, busy, onLongPress } = this.props
    if (holdToTalk || typeof onLongPress !== 'function') return
    e.preventDefault()
    e.stopPropagation()
    this.clearLongPress()
    if (disabled || busy) return
    this.openPanel()
  }

  renderSubmenuHint = () => (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        right: 4,
        bottom: 4,
        display: 'flex',
        gap: 2,
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 3,
            height: 3,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.92)',
            boxShadow: '0 0 2px rgba(0,0,0,0.75)',
          }}
        />
      ))}
    </span>
  )

  render() {
    const {
      holdToTalk = false,
      recording = false,
      busy = false,
      disabled = false,
      iconSize = DEFAULT_ICON,
      color = 'currentColor',
      onLongPress,
      className,
      style,
      showSubmenuHint = true,
    } = this.props

    const isDisabled = !!disabled || !!busy
    const longPressOpensPanel = !holdToTalk && typeof onLongPress === 'function'
    const path = recording ? mdiMicrophoneOff : mdiMicrophone

    const title = busy
      ? 'Procesando nota de voz…'
      : recording
        ? holdToTalk
          ? 'Soltá para finalizar'
          : longPressOpensPanel
            ? 'Click: detener · mantener / clic derecho = panel'
            : 'Detener grabación'
        : holdToTalk
          ? 'Mantener para grabar'
          : longPressOpensPanel
            ? 'Click: grabar · mantener / clic derecho = panel'
            : 'Grabar nota de voz'

    const sharedProps = {
      className,
      'aria-label': title,
      'aria-pressed': !!recording,
      'aria-haspopup': longPressOpensPanel ? 'dialog' : undefined,
      onClick: this.handleClick,
      onPointerDown: this.handlePointerDown,
      onPointerUp: this.handlePointerUp,
      onPointerLeave: this.handlePointerCancel,
      onPointerCancel: this.handlePointerCancel,
      onContextMenu: this.handleContextMenu,
      title: longPressOpensPanel ? title : undefined,
    }

    const mergedStyle = {
      position: 'relative',
      opacity: isDisabled && !recording ? 0.35 : 1,
      color: recording ? '#e53935' : color,
      touchAction: longPressOpensPanel || holdToTalk ? 'none' : undefined,
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      ...style,
    }

    // Botón nativo cuando abre panel: contextmenu fiable (Studio / Edge desktop)
    const control = longPressOpensPanel ? (
      <button
        type="button"
        {...sharedProps}
        disabled={isDisabled && !recording}
        style={{
          ...mergedStyle,
          margin: 0,
          border: 'none',
          background: recording ? 'rgba(229, 57, 53, 0.12)' : 'transparent',
          cursor: isDisabled && !recording ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon path={path} size={iconSize} color={recording ? '#e53935' : color} />
        {showSubmenuHint ? this.renderSubmenuHint() : null}
      </button>
    ) : (
      <IconButton
        {...sharedProps}
        color="inherit"
        disabled={isDisabled && !recording}
        style={mergedStyle}
      >
        <Icon path={path} size={iconSize} color={recording ? '#e53935' : color} />
      </IconButton>
    )

    return (
      <Tooltip title={title} disableTouchListener={longPressOpensPanel}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            minWidth: 0,
            width: longPressOpensPanel ? '100%' : undefined,
          }}
        >
          {control}
        </span>
      </Tooltip>
    )
  }
}

export default VoiceNoteButton
